import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Star, Camera, Upload } from "lucide-react";
import { toast } from "sonner";

function toBase64(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

function Stars({ rating, size = 14, interactive = false, onSelect }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <button
          key={i}
          type="button"
          disabled={!interactive}
          onClick={() => interactive && onSelect?.(i)}
          data-testid={interactive ? `star-${i}` : undefined}
          className={interactive ? "hover:scale-110 transition-transform" : "cursor-default"}
        >
          <Star
            size={size}
            strokeWidth={1.5}
            className={i <= Math.round(rating) ? "text-[hsl(var(--accent))]" : "text-foreground/25"}
            fill={i <= Math.round(rating) ? "hsl(var(--accent))" : "none"}
          />
        </button>
      ))}
    </div>
  );
}

export default function Reviews({ productId }) {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [eligibility, setEligibility] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [f, setF] = useState({ rating: 5, title: "", body: "", images: [] });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const addImages = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const b64s = await Promise.all(files.map(toBase64));
    setF((s) => ({ ...s, images: [...s.images, ...b64s] }));
    e.target.value = "";
  };

  const load = async () => {
    const { data } = await api.get(`/products/${productId}/reviews`);
    setReviews(data);
  };
  const loadEligibility = async () => {
    if (!user) { setEligibility(null); return; }
    try {
      const { data } = await api.get(`/products/${productId}/can-review`);
      setEligibility(data);
    } catch { setEligibility({ eligible: false, reason: "" }); }
  };
  useEffect(() => { load(); loadEligibility(); /* eslint-disable-next-line */ }, [productId, user]);

  const avg = reviews.length ? reviews.reduce((s, r) => s + r.rating, 0) / reviews.length : 0;
  const dist = [5, 4, 3, 2, 1].map((n) => ({
    n, c: reviews.filter((r) => r.rating === n).length,
    pct: reviews.length ? (reviews.filter((r) => r.rating === n).length / reviews.length) * 100 : 0,
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.title.trim() || !f.body.trim()) { toast.error("Add a title and note"); return; }
    setBusy(true);
    try {
      await api.post(`/products/${productId}/reviews`, f);
      toast.success("Review posted");
      setF({ rating: 5, title: "", body: "", images: [] });
      setShowForm(false);
      load();
      loadEligibility();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <section className="mt-24 border-t-2 border-foreground pt-16">
      <div className="grid md:grid-cols-12 gap-12">
        <div className="md:col-span-4">
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">The Wearers</p>
          <h2 className="font-serif text-5xl md:text-6xl mt-2 tracking-tighter leading-none">Reviews.</h2>

          <div className="mt-8 border border-foreground p-6 tag-punch pl-12">
            <div className="flex items-baseline gap-3">
              <p className="font-serif text-6xl">{avg.toFixed(1)}</p>
              <p className="font-mono text-xs text-muted-foreground">/ 5.0</p>
            </div>
            <div className="mt-3"><Stars rating={avg} size={16} /></div>
            <p className="font-mono text-[11px] mt-2 text-muted-foreground">
              Based on {reviews.length} review{reviews.length === 1 ? "" : "s"}
            </p>
            <div className="mt-6 space-y-1.5">
              {dist.map((d) => (
                <div key={d.n} className="flex items-center gap-2 font-mono text-[10px]">
                  <span className="w-4">{d.n}</span>
                  <div className="flex-1 h-1.5 bg-secondary">
                    <div className="h-full bg-foreground" style={{ width: `${d.pct}%` }} />
                  </div>
                  <span className="w-6 text-right text-muted-foreground">{d.c}</span>
                </div>
              ))}
            </div>
          </div>

          {user ? (
            eligibility?.eligible ? (
              <button
                data-testid="write-review-btn"
                onClick={() => setShowForm((s) => !s)}
                className="mt-6 w-full bg-foreground text-background py-3 font-mono text-xs uppercase tracking-[0.24em] hover:bg-[hsl(var(--accent))]"
              >
                {showForm ? "Cancel" : "Write a Review"}
              </button>
            ) : (
              <div className="mt-6 border border-foreground/30 p-4">
                <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Review restricted</p>
                <p className="text-sm mt-1" data-testid="review-restriction-msg">
                  {eligibility?.already_reviewed
                    ? "You've already reviewed this product."
                    : eligibility?.reason || "Only buyers who've purchased and received this product can review it."}
                </p>
              </div>
            )
          ) : (
            <p className="mt-6 font-mono text-[11px] text-muted-foreground">Log in as a buyer who's purchased this to write a review.</p>
          )}
        </div>

        <div className="md:col-span-8">
          {showForm && (
            <form onSubmit={submit} className="border-2 border-foreground p-6 mb-8 tag-punch pl-12">
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-3">Your Rating</p>
              <Stars rating={f.rating} size={22} interactive onSelect={(n) => setF({ ...f, rating: n })} />

              <div className="mt-6">
                <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Headline</label>
                <input
                  data-testid="review-title"
                  value={f.title}
                  onChange={(e) => setF({ ...f, title: e.target.value })}
                  placeholder="Sum it up in a line"
                  className="underline-input"
                />
              </div>
              <div className="mt-4">
                <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Your Notes</label>
                <textarea
                  data-testid="review-body"
                  value={f.body}
                  onChange={(e) => setF({ ...f, body: e.target.value })}
                  placeholder="Fit, comfort, quality — tell it straight."
                  className="underline-input min-h-[100px] w-full"
                />
              </div>

              <div className="mt-4">
                <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Attach Photos (optional)</label>
                <div className="mt-2 grid grid-cols-4 gap-2 mb-3">
                  {f.images.map((src, i) => (
                    <div key={i} className="relative aspect-square bg-secondary">
                      <img src={src} alt="" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        data-testid={`rev-remove-img-${i}`}
                        onClick={() => setF((s) => ({ ...s, images: s.images.filter((_, j) => j !== i) }))}
                        className="absolute top-1 right-1 bg-foreground text-background w-5 h-5 text-xs"
                      >×</button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    data-testid="rev-camera-btn"
                    onClick={() => cameraRef.current?.click()}
                    className="flex-1 border border-foreground py-2 font-mono text-xs uppercase tracking-widest hover:bg-foreground hover:text-background flex items-center justify-center gap-2"
                  >
                    <Camera size={14} /> Take Photo
                  </button>
                  <button
                    type="button"
                    data-testid="rev-upload-btn"
                    onClick={() => fileRef.current?.click()}
                    className="flex-1 border border-foreground py-2 font-mono text-xs uppercase tracking-widest hover:bg-foreground hover:text-background flex items-center justify-center gap-2"
                  >
                    <Upload size={14} /> Upload Files
                  </button>
                  <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={addImages} className="hidden" />
                  <input ref={fileRef} type="file" accept="image/*" multiple onChange={addImages} className="hidden" />
                </div>
              </div>
              <button
                data-testid="submit-review"
                disabled={busy}
                type="submit"
                className="mt-6 bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] px-8 py-3 font-mono text-xs uppercase tracking-[0.24em] hover:bg-foreground hover:text-background disabled:opacity-50"
              >
                {busy ? "Posting..." : "Post Review"}
              </button>
            </form>
          )}

          {reviews.length === 0 ? (
            <div className="border border-foreground/30 p-10">
              <p className="font-serif text-2xl text-muted-foreground">No reviews yet.</p>
              <p className="text-sm mt-2 text-muted-foreground">Be the first to sign off on this one.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {reviews.map((r) => (
                <div key={r.id} data-testid={`review-${r.id}`} className="border-b border-foreground/20 pb-6">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <Stars rating={r.rating} />
                      <h3 className="font-serif text-2xl mt-2 tracking-tighter">{r.title}</h3>
                    </div>
                    <div className="text-right font-mono text-[10px] uppercase tracking-widest text-muted-foreground shrink-0">
                      {new Date(r.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                    </div>
                  </div>
                  <p className="mt-3 text-sm leading-relaxed">{r.body}</p>
                  {r.images?.length > 0 && (
                    <div className="mt-4 flex gap-2 flex-wrap">
                      {r.images.map((src, i) => (
                        <a
                          key={i}
                          href={src}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`rev-img-${r.id}-${i}`}
                          className="w-20 h-20 bg-secondary overflow-hidden border border-foreground/20 hover:border-foreground transition-colors"
                        >
                          <img src={src} alt="review" className="w-full h-full object-cover" />
                        </a>
                      ))}
                    </div>
                  )}
                  <div className="mt-3 flex items-center gap-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    <span>— {r.user_name}</span>
                    {r.verified_purchase && (
                      <span className="text-[hsl(var(--accent))] border border-[hsl(var(--accent))] px-1.5 py-0.5">
                        ✓ Verified Purchase
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
